// @ts-check
import { camelCaseObject, getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

const getApiBaseUrl = () => getConfig().STUDIO_BASE_URL;

function buildQuery(params) {
  return Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
}

export const getCourseMetadataApiUrl = (courseId) =>
  `${getApiBaseUrl()}/api/contentstore/v1/course_metadata/${courseId}`;

export async function getCourseMetadata(courseId) {
  const { data } = await getAuthenticatedHttpClient().get(getCourseMetadataApiUrl(courseId));
  return camelCaseObject(data);
}

export const getCourseOutlineRootApiUrl = (courseId, options = {}) => {
  const {
    childrenLimit = 3,
    childrenOffset = 0,
    format = 'full',
    prefetchDepth = 0,
  } = options;
  const qs = buildQuery({
    children_limit: childrenLimit,
    children_offset: childrenOffset,
    format,
    prefetch_depth: prefetchDepth,
  });
  return `${getApiBaseUrl()}/api/contentstore/v1/course_index/${courseId}/root?${qs}`;
};


export async function getCourseOutlineRoot(courseId, options = {}) {
  const { data } = await getAuthenticatedHttpClient().get(getCourseOutlineRootApiUrl(courseId, options));
  return camelCaseObject(data);
}

// export const getCourseOutlineIndexApiUrl = (courseId) => `${getApiBaseUrl()}/api/contentstore/v1/course_index/${courseId}`;
export const getCourseOutlineIndexApiUrl = (courseId) => `${getApiBaseUrl()}/api/contentstore/v1/course_metadata/${courseId}`;

export const getCourseOutlineChildrenApiUrl = (courseId, options = {}) => {
  const {
    parent, // required to fetch children of a particular section/subsection
    limit = 20,
    offset = 0,
    format = 'full', // or 'concise'
    prefetchDepth = 0,
  } = options;

  const qs = buildQuery({
    limit,
    offset,
    format,
    prefetch_depth: prefetchDepth,
    parent: parent ? parent : undefined,
  });

  return `${getApiBaseUrl()}/api/contentstore/v1/course_index/${courseId}/children${qs ? `?${qs}` : ''}`;
};


export async function getCourseOutlineChildren(courseId, options = {}) {
  const http = getAuthenticatedHttpClient();
  const url = getCourseOutlineChildrenApiUrl(courseId, options);
  const { data } = await http.get(url);
  return camelCaseObject(data);
}

export const getCourseBestPracticesApiUrl = ({
  courseId,
  excludeGraded,
  all,
}) => `${getApiBaseUrl()}/api/courses/v1/quality/${courseId}/?exclude_graded=${excludeGraded}&all=${all}`;

export const getCourseLaunchApiUrl = ({
  courseId,
  gradedOnly,
  validateOras,
  all,
}) => `${getApiBaseUrl()}/api/courses/v1/validation/${courseId}/?graded_only=${gradedOnly}&validate_oras=${validateOras}&all=${all}`;

export const getCourseBlockApiUrl = (courseId) => {
  const formattedCourseId = courseId.split('course-v1:')[1];
  return `${getApiBaseUrl()}/xblock/block-v1:${formattedCourseId}+type@course+block@course`;
};

export const getCourseReindexApiUrl = (reindexLink) => `${getApiBaseUrl()}${reindexLink}`;
export const getXBlockBaseApiUrl = () => `${getApiBaseUrl()}/xblock/`;
export const getCourseItemApiUrl = (itemId) => `${getXBlockBaseApiUrl()}${itemId}`;
export const getXBlockApiUrl = (blockId) => `${getXBlockBaseApiUrl()}outline/${blockId}`;
export const getClipboardUrl = () => `${getApiBaseUrl()}/api/content-staging/v1/clipboard/`;
export const exportTags = (courseId) => `${getApiBaseUrl()}/api/content_tagging/v1/object_tags/${courseId}/export/`;

export const getDemoUnitsApiUrl = () => `${getApiBaseUrl()}/alnafi/api/demo-units/`;

/**
 * Create or update a demo unit record (idempotent by course_id + unit_id).
 * @param {object} payload
 * @param {string} payload.course_id
 * @param {string} payload.section_id
 * @param {string} payload.subsection_id
 * @param {string} payload.unit_id
 * @param {boolean} payload.is_active
 * @returns {Promise<object>}
 */
export async function createOrUpdateDemoUnit(payload) {
  // Temporary debug log for demo unit payload
  // eslint-disable-next-line no-console
  console.log('[demo-units] POST payload', payload);
  const { data } = await getAuthenticatedHttpClient()
    .post(getDemoUnitsApiUrl(), payload);

  return camelCaseObject(data);
}

/**
 * Update a demo unit record by id.
 * @param {string|number} demoUnitId
 * @param {object} payload
 * @param {boolean} payload.is_active
 * @returns {Promise<object>}
 */
export async function updateDemoUnit(demoUnitId, payload) {
  const { data } = await getAuthenticatedHttpClient()
    .patch(`${getDemoUnitsApiUrl()}${demoUnitId}/`, payload);

  return camelCaseObject(data);
}

/**
 * Delete a demo unit record by id.
 * @param {string|number} demoUnitId
 * @returns {Promise<object>}
 */
export async function deleteDemoUnit(demoUnitId) {
  const { data } = await getAuthenticatedHttpClient()
    .delete(`${getDemoUnitsApiUrl()}${demoUnitId}/`);

  return data;
}

/**
 * @typedef {Object} courseOutline
 * @property {string} courseReleaseDate
 * @property {Object} courseStructure
 * @property {Object} deprecatedBlocksInfo
 * @property {string} discussionsIncontextFeedbackUrl
 * @property {string} discussionsIncontextLearnmoreUrl
 * @property {Object} initialState
 * @property {Object} initialUserClipboard
 * @property {string} languageCode
 * @property {string} lmsLink
 * @property {string} mfeProctoredExamSettingsUrl
 * @property {string} notificationDismissUrl
 * @property {string[]} proctoringErrors
 * @property {string} reindexLink
 * @property {null} rerunNotificationId
 */

/**
 * Get course outline index.
 * @param {string} courseId
 * @returns {Promise<courseOutline>}
 */
// export async function getCourseOutlineIndex(courseId) {
//   console.log("----getCourseOutlineIndexApiUrl(courseId)",getCourseOutlineIndexApiUrl(courseId));
//   const { data } = await getAuthenticatedHttpClient()
//   .get(getCourseOutlineIndexApiUrl(courseId));
  
//   console.log("DATA,",data);
//   return camelCaseObject(data);
// }
export async function getCourseOutlineIndex(courseId) {
  const http = getAuthenticatedHttpClient();

  // Launch both requests in parallel; catch individually so one can succeed without the other
  const metadataPromise = http
    .get(getCourseMetadataApiUrl(courseId))
    .catch((err) => ({ __failed: true, err }));
  const rootPromise = http
    .get(
      getCourseOutlineRootApiUrl(courseId, {
        childrenLimit: 6, // default: do not eagerly load sections; change if you want first chunk
        childrenOffset: 0,
        format: 'full', // 'concise' for lighter payload; use 'full' if needed
        prefetchDepth: 0,
      }),
    )
    .catch((err) => ({ __failed: true, err }));

  const [metadataResp, rootResp] = await Promise.all([metadataPromise, rootPromise]);

  // If both failed, fallback to legacy single endpoint (preserve behavior)
  if ((metadataResp && metadataResp.__failed) && (rootResp && rootResp.__failed)) {
    // Throw the more informative error (prefer rootResp error if present)
    try {
      const { data } = await http.get(getCourseOutlineIndexApiUrl(courseId));
      return camelCaseObject(data);
    } catch (fallbackErr) {
      throw rootResp.__failed ? rootResp.err : metadataResp.err;
    }
  }

  const metadataData = (metadataResp && metadataResp.__failed) ? null : (metadataResp && metadataResp.data) || null;
  const rootData = (rootResp && rootResp.__failed) ? null : (rootResp && rootResp.data) || null;

  // Start with rootData so course_structure (hierarchy) is preserved
  const merged = Object.assign({}, rootData || {});

  // If rootData contains `sections` (top-level array) and course_structure exists but lacks child_info.children,
  // create/attach child_info so existing UI (which expects course_structure.child_info.children) continues to work.
  if (rootData) {
    const sections = Array.isArray(rootData.sections) ? rootData.sections : null;
    const pageMeta = rootData.page_meta || rootData.pageMeta || null;

    // Ensure course_structure exists on merged object
    merged.course_structure = merged.course_structure || merged.courseStructure || {};

    // If there's already a child_info (maybe from other backend), prefer it but still merge sections into it if missing
    const existingChildInfo =
      merged.course_structure.child_info ||
      merged.course_structure.childInfo ||
      null;

    if (!existingChildInfo && sections) {
      // Infer category/display_name for child_info from sections if possible, otherwise default to 'chapter'/'Section'
      const inferredCategory = (sections[0] && sections[0].category) || 'chapter';
      const inferredDisplayName = (() => {
        // try to map common container categories to display names; fallback to 'Section'
        if (inferredCategory.toLowerCase().includes('chapter') || inferredCategory.toLowerCase() === 'chapter') return 'Section';
        if (inferredCategory.toLowerCase().includes('sequential') || inferredCategory.toLowerCase() === 'sequential') return 'Unit Group';
        if (inferredCategory.toLowerCase().includes('vertical') || inferredCategory.toLowerCase() === 'vertical') return 'Unit';
        return 'Section';
      })();

      const childInfo = {
        category: inferredCategory,
        display_name: inferredDisplayName,
        children: sections,
      };

      // Add pagination/total if provided
      if (pageMeta) {
        const totalCount = pageMeta.total_count ?? pageMeta.totalCount ?? pageMeta.count ?? null;
        if (totalCount !== null) {
          childInfo.count = totalCount;
        }
        // optionally include limit/offset meta under child_info (not required by UI but may be useful)
        childInfo._page_meta = {
          limit: pageMeta.limit ?? null,
          offset: pageMeta.offset ?? null,
        };
      }

      merged.course_structure.child_info = childInfo;
    } else if (existingChildInfo) {
      // There is an existing child_info: ensure .children exists; if not and root sections present, set them.
      if (!Array.isArray(existingChildInfo.children) && sections) {
        merged.course_structure.child_info = Object.assign({}, existingChildInfo, {
          children: sections,
        });
      }
      // If both exist, do not overwrite children by default. If you want to override, implement policy here.
    }

    // Also make sure that if rootData.sections exist but course_structure.child_info.children already exists,
    // we don't duplicate. If you want to append rather than replace, implement merging logic here.
  }

  // Overlay metadata fields at top-level so UI pieces relying on metadata (release date, etc.) get them quickly.
  if (metadataData) {
    // Copy top-level keys from metadataData that are not course_structure (we preserve course_structure from rootData)
    Object.keys(metadataData).forEach((k) => {
      if (k === 'course_structure' || k === 'courseStructure') return;
      // Only set if metadata key has value (metadata should override root for non-structure fields)
      merged[k] = metadataData[k];
    });

    // If merged doesn't already have course_structure, and metadata has a course_structure, use it (unlikely)
    if (!merged.course_structure && (metadataData.course_structure || metadataData.courseStructure)) {
      merged.course_structure = metadataData.course_structure || metadataData.courseStructure;
    }
  }
  console.log("Merged Data",merged)
  // Final normalization: return camelCased object for compatibility with codebase
  return camelCaseObject(merged);
}

/**
 * Get course best practices.
 * @param {{courseId: string, excludeGraded: boolean, all: boolean}} options
 * @returns {Promise<{isSelfPaced: boolean, sections: any, subsection: any, units: any, videos: any }>}
 */
export async function getCourseBestPractices({
  courseId,
  excludeGraded,
  all,
}) {
  const { data } = await getAuthenticatedHttpClient()
    .get(getCourseBestPracticesApiUrl({ courseId, excludeGraded, all }));

  return camelCaseObject(data);
}

/** @typedef {object} courseLaunchData
 * @property {boolean} isSelfPaced
 * @property {object} dates
 * @property {object} assignments
 * @property {object} grades
 * @property {number} grades.sum_of_weights
 * @property {object} certificates
 * @property {object} updates
 * @property {object} proctoring
 */

/**
 * Get course launch.
 * @param {{courseId: string, gradedOnly: boolean, validateOras: boolean, all: boolean}} options
 * @returns {Promise<courseLaunchData>}
 */
export async function getCourseLaunch({
  courseId,
  gradedOnly,
  validateOras,
  all,
}) {
  const { data } = await getAuthenticatedHttpClient()
    .get(getCourseLaunchApiUrl({
      courseId, gradedOnly, validateOras, all,
    }));

  return camelCaseObject(data);
}

/**
 * Enable course highlights emails
 * @param {string} courseId
 * @returns {Promise<Object>}
 */
export async function enableCourseHighlightsEmails(courseId) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseBlockApiUrl(courseId), {
      publish: 'republish',
      metadata: {
        highlights_enabled_for_messaging: true,
      },
    });

  return data;
}

/**
 * Restart reindex course
 * @param {string} reindexLink
 * @returns {Promise<Object>}
 */
export async function restartIndexingOnCourse(reindexLink) {
  const { data } = await getAuthenticatedHttpClient()
    .get(getCourseReindexApiUrl(reindexLink));

  return camelCaseObject(data);
}

/**
 * @typedef {Object} section
 * @property {string} id
 * @property {string} displayName
 * @property {string} category
 * @property {boolean} hasChildren
 * @property {string} editedOn
 * @property {boolean} published
 * @property {string} publishedOn
 * @property {string} studioUrl
 * @property {boolean} releasedToStudents
 * @property {string} releaseDate
 * @property {string} visibilityState
 * @property {boolean} hasExplicitStaffLock
 * @property {string} start
 * @property {boolean} graded
 * @property {string} dueDate
 * @property {null} due
 * @property {null} relativeWeeksDue
 * @property {null} format
 * @property {string[]} courseGraders
 * @property {boolean} hasChanges
 * @property {object} actions
 * @property {null} explanatoryMessage
 * @property {object[]} userPartitions
 * @property {string} showCorrectness
 * @property {string[]} highlights
 * @property {boolean} highlightsEnabled
 * @property {boolean} highlightsPreviewOnly
 * @property {string} highlightsDocUrl
 * @property {object} childInfo
 * @property {boolean} ancestorHasStaffLock
 * @property {boolean} staffOnlyMessage
 * @property {boolean} hasPartitionGroupComponents
 * @property {object} userPartitionInfo
 * @property {boolean} enableCopyPasteUnits
 */

/**
 * Get course section
 * @param {string} itemId
 * @returns {Promise<section>}
 */
export async function getCourseItem(itemId) {
  const { data } = await getAuthenticatedHttpClient()
    .get(getXBlockApiUrl(itemId));
  return camelCaseObject(data);
}

/**
 * Update course section highlights
 * @param {string} sectionId
 * @param {Array<string>} highlights
 * @returns {Promise<Object>}
 */
export async function updateCourseSectionHighlights(sectionId, highlights) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseItemApiUrl(sectionId), {
      publish: 'republish',
      metadata: {
        highlights,
      },
    });

  return data;
}

/**
 * Publish course section
 * @param {string} sectionId
 * @returns {Promise<Object>}
 */
export async function publishCourseSection(sectionId) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseItemApiUrl(sectionId), {
      publish: 'make_public',
    });

  return data;
}

/**
 * Configure course section
 * @param {string} sectionId
 * @param {boolean} isVisibleToStaffOnly
 * @param {string} startDatetime
 * @returns {Promise<Object>}
 */
export async function configureCourseSection(sectionId, isVisibleToStaffOnly, startDatetime) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseItemApiUrl(sectionId), {
      publish: 'republish',
      metadata: {
        // The backend expects metadata.visible_to_staff_only to either true or null
        visible_to_staff_only: isVisibleToStaffOnly ? true : null,
        start: startDatetime,
      },
    });

  return data;
}

/**
 * Configure course section
 * @param {string} itemId
 * @param {string} isVisibleToStaffOnly
 * @param {string} releaseDate
 * @param {string} graderType
 * @param {string} dueDate
 * @param {boolean} isProctoredExam,
 * @param {boolean} isOnboardingExam,
 * @param {boolean} isPracticeExam,
 * @param {string} examReviewRules,
 * @param {boolean} isTimeLimited
 * @param {number} defaultTimeLimitMin
 * @param {string} hideAfterDue
 * @param {string} showCorrectness
 * @param {boolean} isPrereq,
 * @param {string} prereqUsageKey,
 * @param {number} prereqMinScore,
 * @param {number} prereqMinCompletion,
 * @returns {Promise<Object>}
 */
export async function configureCourseSubsection(
  itemId,
  isVisibleToStaffOnly,
  releaseDate,
  graderType,
  dueDate,
  isTimeLimited,
  isProctoredExam,
  isOnboardingExam,
  isPracticeExam,
  examReviewRules,
  defaultTimeLimitMin,
  hideAfterDue,
  showCorrectness,
  isPrereq,
  prereqUsageKey,
  prereqMinScore,
  prereqMinCompletion,
) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseItemApiUrl(itemId), {
      publish: 'republish',
      graderType,
      isPrereq,
      prereqUsageKey,
      prereqMinScore,
      prereqMinCompletion,
      metadata: {
        // The backend expects metadata.visible_to_staff_only to either true or null
        visible_to_staff_only: isVisibleToStaffOnly ? true : null,
        due: dueDate,
        hide_after_due: hideAfterDue,
        show_correctness: showCorrectness,
        is_practice_exam: isPracticeExam,
        is_time_limited: isTimeLimited,
        is_proctored_enabled: isProctoredExam || isPracticeExam || isOnboardingExam,
        exam_review_rules: examReviewRules,
        default_time_limit_minutes: defaultTimeLimitMin,
        is_onboarding_exam: isOnboardingExam,
        start: releaseDate,
      },
    });
  return data;
}

/**
 * Configure course unit
 * @param {string} unitId
 * @param {boolean} isVisibleToStaffOnly
 * @param {object} groupAccess
 * @returns {Promise<Object>}
 */
export async function configureCourseUnit(unitId, isVisibleToStaffOnly, groupAccess) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseItemApiUrl(unitId), {
      publish: 'republish',
      metadata: {
        // The backend expects metadata.visible_to_staff_only to either true or null
        visible_to_staff_only: isVisibleToStaffOnly ? true : null,
        group_access: groupAccess,
      },
    });

  return data;
}

/**
 * Edit course section
 * @param {string} itemId
 * @param {string} displayName
 * @returns {Promise<Object>}
 */
export async function editItemDisplayName(itemId, displayName,sectionNumber) {
  console.log("======================",getCourseItemApiUrl(itemId))
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseItemApiUrl(itemId), {
      metadata: {
        display_name: displayName,
        days_of_access: sectionNumber,

      },
    });
    console.log("API data",data)
  return data;
}

/**
 * Delete course section
 * @param {string} itemId
 * @returns {Promise<Object>}
 */
export async function deleteCourseItem(itemId) {
  const { data } = await getAuthenticatedHttpClient()
    .delete(getCourseItemApiUrl(itemId));

  return data;
}

/**
 * Duplicate course section
 * @param {string} itemId
 * @param {string} parentId
 * @returns {Promise<Object>}
 */
export async function duplicateCourseItem(itemId, parentId) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getXBlockBaseApiUrl(), {
      duplicate_source_locator: itemId,
      parent_locator: parentId,
    });

  return data;
}

/**
 * Add new course item like section, subsection or unit.
 * @param {string} parentLocator
 * @param {string} category
 * @param {string} displayName
 * @returns {Promise<Object>}
 */
export async function addNewCourseItem(parentLocator, category, displayName) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getXBlockBaseApiUrl(), {
      parent_locator: parentLocator,
      category,
      display_name: displayName,
    });

  return data;
}

/**
 * Set order for the list of the sections
 * @param {string} courseId
 * @param {Array<string>} children list of sections id's
 * @returns {Promise<Object>}
*/
export async function setSectionOrderList(courseId, children) {
  const { data } = await getAuthenticatedHttpClient()
    .put(getCourseBlockApiUrl(courseId), {
      children,
    });

  return data;
}

/**
 * Set order for the list of the subsections
 * @param {string} itemId Subsection or unit ID
 * @param {Array<string>} children list of sections id's
 * @returns {Promise<Object>}
*/
export async function setCourseItemOrderList(itemId, children) {
  const { data } = await getAuthenticatedHttpClient()
    .put(getCourseItemApiUrl(itemId), {
      children,
    });

  return data;
}

/**
 * Set video sharing setting
 * @param {string} courseId
 * @param {string} videoSharingOption
 * @returns {Promise<Object>}
*/
export async function setVideoSharingOption(courseId, videoSharingOption) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getCourseBlockApiUrl(courseId), {
      metadata: {
        video_sharing_options: videoSharingOption,
      },
    });

  return data;
}

/**
 * Paste block to clipboard
 * @param {string} parentLocator
 * @returns {Promise<Object>}
*/
export async function pasteBlock(parentLocator) {
  const { data } = await getAuthenticatedHttpClient()
    .post(getXBlockBaseApiUrl(), {
      parent_locator: parentLocator,
      staged_content: 'clipboard',
    });

  return camelCaseObject(data);
}

/**
 * Dismiss notification
 * @param {string} url
 * @returns void
*/
export async function dismissNotification(url) {
  await getAuthenticatedHttpClient()
    .delete(url);
}

/**
 * Downloads the file of the exported tags
 * @param {string} courseId The ID of the content
 * @returns void
 */
export async function getTagsExportFile(courseId, courseName) {
  // Gets exported tags and builds the blob to download CSV file.
  // This can be done with this code:
  // `window.location.href = exportTags(contentId);`
  // but it is done in this way so we know when the operation ends to close the toast.
  const response = await getAuthenticatedHttpClient().get(exportTags(courseId), {
    responseType: 'blob',
  });

  /* istanbul ignore next */
  if (response.status !== 200) {
    throw response.statusText;
  }

  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `${courseName}.csv`;
  a.click();

  window.URL.revokeObjectURL(url);
}
